import React, { useContext } from 'react';
import { Users } from 'styled-icons/fa-solid';
import { User as TotemUser } from 'lib/User';
import { UserContext } from 'constants/user-context';

import { PrivilegesTable } from 'pages/Settings/SettingsPage/SettingsTeam/SettingsTeamPrivileges';
import { UsersTable } from 'pages/Settings/SettingsPage/SettingsTeam/SettingsTeamUsers';
import { EmployeesTable } from 'pages/Settings/SettingsPage/SettingsTeam/SettingsTeamEmployees';

import { Loading } from 'components/Loading';

import {
    SettingsTitleContainerWrapper,
    SettingsTitleContainer,
    SettingsTitle,
    SettingsSubtitle,
    SettingsPageStyle,
    SettingsCard,
} from 'styles/pages/settings.styles';

import {
    GetUserQuery,
    GetUsersOfOrganizationQuery,
    TotemFragmentFragment as Totem,
    useGetUserGroupsQuery,
    useGetUsersOfOrganizationQuery,
} from 'data/__generated__';

interface SettingsPageTeamProps {
    totems: Totem[] | undefined;
    pageKey?: string;
    orderId?: string;
    route?: any;
    user: GetUserQuery['user'] | undefined;
}

export type MinimalUser = GetUsersOfOrganizationQuery['usersOfOrganization'][number];

export const SettingsPageTeam: React.FC<SettingsPageTeamProps> = ({ totems }) => {
    const { user: currentUser, organization } = useContext(UserContext);
    const organizationId = organization?._id ?? '';
    const maxUsers = organization?.maxUsers ?? 0;
    const { error, loading, data, refetch: refetchUsers } = useGetUsersOfOrganizationQuery();
    const {
        error: userGroupError,
        loading: userGroupLoading,
        data: userGroupData,
    } = useGetUserGroupsQuery({ variables: { organizationId } });

    if (error || userGroupError) {
        throw new Error();
    }
    if (loading || userGroupLoading) {
        return <Loading relative />;
    }

    const { usersOfOrganization } = data! as GetUsersOfOrganizationQuery;
    const { userGroups } = userGroupData!;

    const { teamUsers, employeeUsers } = usersOfOrganization
        .filter((user) => !!user?.profile?.organizationId)
        .reduce<{
            teamUsers: MinimalUser[];
            employeeUsers: MinimalUser[];
        }>(
            (userTypes, user) => {
                if (currentUser && currentUser._id === user?._id) {
                    userTypes.teamUsers.unshift(user);
                } else if (TotemUser.isGlobalTeamMember(user?.roles)) {
                    userTypes.teamUsers.push(user);
                } else {
                    userTypes.employeeUsers.push(user);
                }
                return userTypes;
            },
            {
                teamUsers: [],
                employeeUsers: [],
            },
        );

    return (
        <SettingsPageStyle>
            <SettingsCard>
                <SettingsTitleContainerWrapper>
                    <SettingsTitleContainer>
                        <Users />
                        <SettingsTitle>Utilisateurs</SettingsTitle>
                    </SettingsTitleContainer>
                </SettingsTitleContainerWrapper>
                <SettingsSubtitle>Gérez ici votre équipe : ajoutez jusqu'à {maxUsers} utilisateurs.</SettingsSubtitle>
                <SettingsSubtitle>Gérez aussi les droits d'accès : Admin, Editeur ou Analyste.</SettingsSubtitle>
                <PrivilegesTable />
            </SettingsCard>
            <SettingsCard>
                <UsersTable users={teamUsers} totems={totems} />
            </SettingsCard>
            <SettingsCard>
                <EmployeesTable
                    users={[...teamUsers, ...employeeUsers]}
                    userGroups={userGroups}
                    refetchUsers={refetchUsers}
                />
            </SettingsCard>
        </SettingsPageStyle>
    );
};
